import {
  FormGroup,
  Validators,
  FormControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  signal,
  OnInit,
  inject,
  Component,
  computed,
  DestroyRef,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { Store } from '@ngrx/store';
import { switchMap, filter, tap, catchError, of, skip } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import {
  DialogCloseReason,
  DialogCloseOptions,
  injectDirtyCloseHandler,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
  SuiDialogConfig,
} from '@sui/dialog';
import { ComboboxOption, SuiComboboxComponent } from '@sui/combobox';

import { AutoStoryPayload } from '../../../core/services/feed.service';
import { UtilsApiService } from 'src/app/api/services/utils-api.service';
import {
  PromptParameter,
  PromptParametersSchema,
} from 'src/app/api/services/feed-api.service';

import { Feed } from '../../../core/models/core.models';
import { injectLlmModelsQuery } from '../../../api/queries/nomenclature.queries';
import {
  LlmPromptTemplate,
  NomenclatureApiService,
} from '../../../api/services/nomenclature-api.service';
import { SimpleDialogBase } from '../../abstract/simple-dialog-base';

import { AppState } from '../../../core/state/app.state';
import {
  selectFeed,
  getContentFilters,
  selectUserAccessLevelGlobalAdmin,
} from '../../../core/state';
import { SuiButtonComponent } from '@sui/button';
import {
  SuiDetailItemComponent,
  SuiDetailListComponent,
} from '@sui/detail-list';
import { NgpLabel } from 'ng-primitives/form-field';
import { SuiFormFieldComponent, SuiLabelDirective } from '@sui/form-field';
import { SuiTextareaComponent } from '@sui/textarea';
import { SuiInputComponent } from '@sui/input';
import { SuiSelectComponent, SuiSelectOptionComponent } from '@sui/select';
import { SuiCheckboxComponent } from '@sui/checkbox';
import { AlgorithmScheduleComponent } from '../algorithm-schedule/algorithm-schedule.component';
import { CurrencyPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

interface AutoStoryToolDialogInput {
  initialPayload?: Partial<AutoStoryPayload>;
  canSchedule?: boolean;
  editMode?: boolean;
  standalone?: boolean; // opened outside a feed view (e.g. the runs grid); submit must not require feed state
}

interface AutoStoryToolDialogOutput {
  payload: AutoStoryPayload;
}

@Component({
  selector: 'app-auto-story-tool-dialog',
  templateUrl: './auto-story-tool-dialog.component.html',
  styleUrl: './auto-story-tool-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiFormFieldComponent,
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    SuiButtonComponent,
    SuiDetailListComponent,
    SuiDetailItemComponent,
    SuiDialogContentSlot,
    FormsModule,
    ReactiveFormsModule,
    NgpLabel,
    SuiLabelDirective,
    SuiComboboxComponent,
    SuiTextareaComponent,
    SuiInputComponent,
    SuiSelectComponent,
    SuiSelectOptionComponent,
    SuiCheckboxComponent,
    AlgorithmScheduleComponent,
    SuiDialogFooterSlot,
    CurrencyPipe,
    TranslatePipe,
  ],
})
export class AutoStoryToolDialogComponent
  extends SimpleDialogBase<AutoStoryToolDialogInput, AutoStoryToolDialogOutput>
  implements OnInit, AfterViewInit
{
  static dialogConfig: Partial<SuiDialogConfig> = { width: 600 };

  protected override dirtyHandler = injectDirtyCloseHandler(this.dialogRef);
  autoStoryForm: FormGroup;
  promptParametersForm: FormGroup;

  // * Form Options - select menu models
  sortFields = [
    {
      id: 'volume',
      name: this.translateService.instant('auto-story-tool.sort-fields.volume'),
    },
    {
      id: 'potential_reach',
      name: this.translateService.instant(
        'auto-story-tool.sort-fields.potential-reach',
      ),
    },
    {
      id: 'estimated_reach',
      name: this.translateService.instant(
        'auto-story-tool.sort-fields.estimated-reach',
      ),
    },
    {
      id: 'engagement',
      name: this.translateService.instant(
        'auto-story-tool.sort-fields.engagement',
      ),
    },
  ];

  // * 'cosine' (embedding-based sampling) is deliberately absent until SEN-5139 ships it
  sampleMetrics = [
    { id: '', name: 'Disabled' },
    {
      id: 'volume',
      name: this.translateService.instant(
        'auto-story-tool.sample-metrics.volume',
      ),
    },
    {
      id: 'potential_reach',
      name: this.translateService.instant(
        'auto-story-tool.sample-metrics.potential-reach',
      ),
    },
    {
      id: 'estimated_reach',
      name: this.translateService.instant(
        'auto-story-tool.sample-metrics.estimated-reach',
      ),
    },
    {
      id: 'engagement',
      name: this.translateService.instant(
        'auto-story-tool.sample-metrics.engagement',
      ),
    },
    {
      id: 'published',
      name: this.translateService.instant(
        'auto-story-tool.sample-metrics.time-published',
      ),
    },
  ];

  sampleMetricId: string = ''; // empty = disabled

  private llmModelsQuery = injectLlmModelsQuery();

  // * Model override options, served by the backend already filtered and ordered.
  // * The leading empty value is not a model - it means "use the model defined on
  // * the selected prompt template", so it is prepended here rather than coming
  // * from the API, and it stays an option so the default can be picked again.
  protected modelOptions = computed<ComboboxOption[]>(() => [
    {
      value: '',
      label: this.translateService.instant(
        'auto-story-tool.models.default-from-prompt',
      ),
    },
    ...(this.llmModelsQuery.data() ?? []).map((llmModel) => ({
      value: llmModel.vendor_model_id,
      label: llmModel.name,
    })),
  ]);

  protected prompts = signal<LlmPromptTemplate[]>([]);

  protected promptOptions = computed<ComboboxOption[]>(() =>
    this.prompts().map((prompt) => ({ value: prompt.id, label: prompt.name })),
  );

  // * mirrors of the two form controls the model preview depends on - a FormControl
  // * is not reactive to computed(), so the values are pushed in from valueChanges.
  private selectedPromptId = signal<string | number | null>(null);
  private selectedModelId = signal<string>('');

  /**
   * The model a run would actually use, as a vendor model id: the explicit
   * override when one is picked, otherwise the model the selected prompt
   * template declares. Empty until a prompt is chosen.
   */
  private targetModelId = computed(() => {
    const overrideId = this.selectedModelId();
    if (overrideId !== '') {
      return overrideId;
    }

    const promptId = this.selectedPromptId();

    return (
      this.prompts().find((prompt) => prompt.id == promptId)?.vendor_model_id ??
      ''
    );
  });

  /** The catalogue entry for targetModelId, or null when it is not selectable. */
  protected effectiveModel = computed(() => {
    const modelId = this.targetModelId();

    return modelId === ''
      ? null
      : ((this.llmModelsQuery.data() ?? []).find(
          (llmModel) => llmModel.vendor_model_id === modelId,
        ) ?? null);
  });

  /**
   * The model id a run would use that is no longer selectable, or '' when
   * there is nothing to warn about. A template can still name a model that has
   * since been deactivated - the binding records which model it is, not whether
   * we are willing to call it - and the run then fails in the worker rather
   * than here. Blank while the catalogue is still loading or failed to load,
   * since absent data is not evidence the model is gone.
   */
  protected unavailableModelId = computed(() => {
    const modelId = this.targetModelId();
    const catalogue = this.llmModelsQuery.data();

    return modelId !== '' &&
      catalogue !== undefined &&
      this.effectiveModel() === null
      ? modelId
      : '';
  });

  /** true when the override select is on "Default from Prompt" */
  protected isModelFromPrompt = computed(() => this.selectedModelId() === '');
  promptParameters: PromptParametersSchema = {};
  promptParameterControls: Array<{
    key: string;
    type: string;
    min: number | null;
    max: number | null;
    options: Array<{ id: number; name: string }>;
  }> = [];
  hasPromptParameters = false;

  currentFeed: Feed | null = null;
  contentFilters: any = null;
  isLoading = false;

  get canSchedule(): boolean {
    return !!this.dialogData?.canSchedule;
  }
  get editMode(): boolean {
    return !!this.dialogData?.editMode;
  }
  get showSchedule(): boolean {
    return this.canSchedule || this.editMode;
  }
  get initialIntervalMinutes(): number {
    const value = this.dialogData?.initialPayload?.schedule_interval_minutes;
    return typeof value === 'number' && value > 0 ? value : 60;
  }
  get initialRecurrence() {
    return (
      (this.dialogData?.initialPayload as any)?.['schedule_recurrence'] ?? null
    );
  }
  get initialTimezoneId() {
    return (
      (this.dialogData?.initialPayload as any)?.['schedule_timezone_id'] ?? null
    );
  }

  protected isDirty = signal(false);
  protected isValid = signal(false);
  protected scheduleMode = signal(false);

  protected runButtonText = computed(() => {
    if (this.editMode) {
      return this.translateService.instant('btn.save');
    }
    if (this.canSchedule && this.scheduleMode()) {
      return this.translateService.instant('btn.schedule');
    }
    if (this.canSchedule) {
      return this.translateService.instant('btn.run-now');
    }
    return this.translateService.instant('auto-story-tool.form-button.run');
  });

  protected runButtonIcon = computed<IconProp | undefined>(() => {
    if (this.editMode) return undefined;
    if (!this.canSchedule) return undefined;
    return this.scheduleMode() ? ['fas', 'clock'] : ['fas', 'play'];
  });

  private destroyRef = inject(DestroyRef);
  private formBuilder = inject(FormBuilder);
  private utilsApi = inject(UtilsApiService);
  private nomenclatureApi = inject(NomenclatureApiService);
  private store = inject(Store<AppState>);

  // * model override selection is restricted to global admins
  protected isGlobalAdmin = toSignal(
    this.store.select(selectUserAccessLevelGlobalAdmin),
    { initialValue: false },
  );

  ngOnInit(): void {
    this.initializeForm();
    this.applyInitialPayload();
    this.loadPrompts();
    this.setupPromptChangeListener();
    this.loadFeedContext();
    this.setupFormChangeTracking();
  }

  private applyInitialPayload(): void {
    const initial = this.dialogData?.initialPayload;
    if (!initial) {
      return;
    }

    const { prompt_params, ...mainFields } = initial;
    // * stored payloads may still carry 'cosine'; the option is hidden until SEN-5139
    if (mainFields.sample_metric === 'cosine') {
      mainFields.sample_metric = '';
    }
    // * legacy 'reach' always exported post_reach_v2, which is the estimated reach
    if (mainFields.sort === 'reach') {
      mainFields.sort = 'estimated_reach';
    }
    if (mainFields.sample_metric === 'reach') {
      mainFields.sample_metric = 'estimated_reach';
    }
    this.autoStoryForm.patchValue(mainFields);

    if (typeof mainFields.sample_metric === 'string') {
      this.sampleMetricId = mainFields.sample_metric;
    }
  }

  ngAfterViewInit(): void {
    this.setupDirtyHandler(this.isDirty, this.isValid);
  }

  private initializeForm(): void {
    this.autoStoryForm = this.formBuilder.group({
      prompt_template_id: ['', Validators.required],
      model: [''], // empty = use the prompt template's own model
      clusters: [
        10,
        [Validators.required, Validators.min(1), Validators.max(500)],
      ],
      distance_threshold: [
        35,
        [Validators.required, Validators.min(1), Validators.max(100)],
      ],
      sanitize_mentions: [true],
      sanitize_urls: [true],
      sort: ['volume', Validators.required],
      sample_metric: [''],
      sampling_percentile_groups: [
        3,
        [Validators.required, Validators.min(1), Validators.max(10)],
      ],
      sampling_percentage_per_group: [
        20,
        [Validators.required, Validators.min(1), Validators.max(100)],
      ],
      sample_length: [0, [Validators.required, Validators.min(0)]],
      sampling_time_buckets: [false],
      create_story_posts: [true],
      send_notification: [true],
    });

    this.promptParametersForm = this.formBuilder.group({});
  }

  private setupFormChangeTracking(): void {
    this.autoStoryForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), skip(1))
      .subscribe(() => {
        this.isDirty.set(true);
        this.isValid.set(
          this.autoStoryForm.valid && this.promptParametersForm.valid,
        );
        this.scheduleMode.set(!!this.autoStoryForm.get('schedule')?.value);
      });

    // sui-select is CVA-only (no native change event) — mirror the selected
    // sampling metric off the control's value stream (was the template change).
    this.autoStoryForm
      .get('sample_metric')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => (this.sampleMetricId = value));

    // same reason for the two controls the model preview reads - seeded from the
    // current value first, because valueChanges does not replay what
    // applyInitialPayload() already patched in.
    this.selectedPromptId.set(
      this.autoStoryForm.get('prompt_template_id')?.value ?? null,
    );
    this.autoStoryForm
      .get('prompt_template_id')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.selectedPromptId.set(value ?? null));

    this.selectedModelId.set(this.autoStoryForm.get('model')?.value ?? '');
    this.autoStoryForm
      .get('model')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => this.selectedModelId.set(value ?? ''));

    this.promptParametersForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isDirty.set(true);
        this.isValid.set(
          this.autoStoryForm.valid && this.promptParametersForm.valid,
        );
      });
  }

  private loadPrompts(): void {
    this.isLoading = true;
    this.nomenclatureApi
      .getLlmPromptTemplates()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((prompts) => {
          this.prompts.set(prompts ?? []);
          this.isLoading = false;

          const initialPromptId =
            this.dialogData?.initialPayload?.prompt_template_id;
          const hasInitialPrompt =
            initialPromptId != null &&
            prompts?.some((p) => p.id === initialPromptId);

          if (hasInitialPrompt) {
            this.autoStoryForm.patchValue({
              prompt_template_id: initialPromptId,
            });
          } else if (prompts && prompts.length > 0) {
            // * auto-select First Prompt if available
            this.autoStoryForm.patchValue({ prompt: prompts[0].id });
          }
        }),
        catchError((error) => {
          // TODO: handle error
          this.isLoading = false;
          return of(null);
        }),
      )
      .subscribe();
  }

  private setupPromptChangeListener(): void {
    this.autoStoryForm
      // template text from the nomenclature
      .get('prompt_template_id')
      ?.valueChanges.pipe(
        filter((promptId) => !!promptId),
        switchMap((promptId) => {
          this.promptParameters = {};
          this.promptParametersForm = this.formBuilder.group({});
          this.promptParameterControls = [];
          this.hasPromptParameters = false;

          return this.utilsApi.getCustomAutoStoryPromptParameters(
            promptId.toString(),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
        tap((parameters) => {
          this.promptParameters = parameters;
          this.buildPromptParametersForm(parameters);
          this.buildPromptParameterControls();
          this.hasPromptParameters = this.promptParameterControls.length > 0;

          const initialPromptParams =
            this.dialogData?.initialPayload?.prompt_params;
          if (initialPromptParams) {
            this.promptParametersForm.patchValue(initialPromptParams);
          }
        }),
        catchError((error) => {
          // * no parameters, just continue with empty Form
          this.promptParameters = {};
          this.promptParameterControls = [];
          this.hasPromptParameters = false;
          return of(null);
        }),
      )
      .subscribe();
  }

  private buildPromptParametersForm(parameters: PromptParametersSchema): void {
    const formControls: { [key: string]: FormControl } = {};

    Object.keys(parameters).forEach((key) => {
      const param = parameters[key];
      const defaultValue = param.value || param.default;
      const validators = this.buildValidators(param);

      formControls[key] = new FormControl(defaultValue, validators);
    });

    this.promptParametersForm = this.formBuilder.group(formControls);
  }

  private buildValidators(param: PromptParameter): any[] {
    const validators = [];

    if (param.validators) {
      param.validators.forEach((validator) => {
        switch (validator.type) {
          case 'MinValidator':
            validators.push(Validators.min(validator.args.min));
            break;
          case 'MaxValidator':
            validators.push(Validators.max(validator.args.max));
            break;
        }
      });
    }

    return validators;
  }

  private loadFeedContext(): void {
    this.store
      .select(selectFeed)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((feed) => {
        this.currentFeed = feed;
      });

    // * get Content Filters (includes time period)
    this.store
      .select(getContentFilters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filters) => {
        this.contentFilters = filters;
      });
  }

  async onCancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }

  onSubmit(): void {
    const hasFeedContext = !!(this.currentFeed && this.contentFilters);
    if (
      this.autoStoryForm.valid &&
      (hasFeedContext || this.dialogData?.standalone)
    ) {
      const payload = this.buildPayload();
      this.dirtyHandler.closeImmediately({
        reason: DialogCloseReason.Save,
        data: { payload },
      });
    }
  }

  protected override performSave(): DialogCloseOptions<AutoStoryToolDialogOutput> {
    const payload = this.buildPayload();
    return {
      reason: DialogCloseReason.Save,
      data: { payload },
    };
  }

  private buildPayload(): AutoStoryPayload {
    const formData = this.autoStoryForm.value;
    const promptParams = this.promptParametersForm.value;

    const payload: AutoStoryPayload = {
      prompt_template_id: formData.prompt_template_id,
      clusters: formData.clusters,
      distance_threshold: formData.distance_threshold,
      sanitize_mentions: formData.sanitize_mentions,
      sanitize_urls: formData.sanitize_urls,
      sort: formData.sort,
      sample_metric: formData.sample_metric,
      sampling_percentile_groups: formData.sampling_percentile_groups,
      sampling_percentage_per_group: formData.sampling_percentage_per_group,
      sampling_time_buckets: !!formData.sampling_time_buckets,
      sample_length: formData.sample_length,
      create_story_posts: !!formData.create_story_posts,
      send_notification: !!formData.send_notification,
      schedule: !!formData.schedule,
    };

    if (formData.schedule) {
      payload.schedule_recurrence = formData.schedule_recurrence;
      payload.schedule_timezone_id = formData.schedule_timezone_id;
    }

    // * only send a model override when explicitly selected; empty = backend uses the prompt template's model
    if (formData.model) {
      payload.model = formData.model;
    }

    if (Object.keys(promptParams).length > 0) {
      payload.prompt_params = promptParams;
    }

    return payload;
  }

  // * Helper methods for template
  private buildPromptParameterControls(): void {
    const controls = [];
    Object.keys(this.promptParametersForm.controls).forEach((key) => {
      const control = this.promptParametersForm.get(key);
      if (control) {
        controls.push({
          key,
          type: this.getControlType(key),
          min: this.getControlAttribute(key, 'min'),
          max: this.getControlAttribute(key, 'max'),
          options: this.getControlOptions(key),
        });
      }
    });
    this.promptParameterControls = controls;
  }

  getParameterLabel(key: string): string {
    return this.translateService.instant(
      `auto-story-tool.prompt-parameter.label`,
      { key },
    );
  }

  private getControlType(key: string): string {
    const param = this.promptParameters[key];
    if (!param) return 'number';

    if (param.type === 'Integer') {
      return 'number';
    } else if (param.type && param.type.startsWith('Nomenclature.')) {
      return 'select';
    }

    return 'number';
  }

  private getControlAttribute(key: string, attribute: string): number | null {
    const param = this.promptParameters[key];
    if (!param || !param.attributes) return null;

    return param.attributes[attribute] || null;
  }

  private getControlOptions(key: string): Array<{ id: number; name: string }> {
    const param = this.promptParameters[key];
    if (!param || !param.options) return [];

    return param.options;
  }

  getSelectedPromptPreview(): string {
    const promptId = this.autoStoryForm.get('prompt_template_id')?.value;

    if (!promptId) return '';

    return (
      this.prompts().find((prompt) => prompt.id == promptId)?.template ?? ''
    );
  }
}
